import React from 'react';
import { Icon } from 'js/components';
import { faChartNetwork } from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-solid-svg-icons';
import CapabilityOverviewBar from './CapabilityOverviewBar';
import { enabledThresholdValue } from 'js/utils/capability-treemap-scores';
import styles from './CapabilityOverviewCharts.module.scss';

function EmptyState({ children }) {
  return (
    <div className="media text-muted mt-4 mb-4">
      <Icon icon={faChartNetwork} size="4x" className="mr-3" fixedWidth />
      <div className="media-body">{children}</div>
    </div>
  );
}

export default function CapabilityOverviewChartGrid({
  elements = [],
  capabilities = [],
  scoresByCapability,
  capabilitySetName,
  emptyReason = null,
  thresholds = {},
}) {
  if (elements.length === 0) {
    return (
      <EmptyState>
        <p>No scored capability elements yet.</p>
        <p>
          Once assessments include scored questions for this set, charts will
          appear here.
        </p>
      </EmptyState>
    );
  }

  if (capabilities.length === 0) {
    if (emptyReason === 'none-exist') {
      return (
        <EmptyState>
          <p>This capability set doesn&apos;t have any capabilities yet.</p>
          <p>Add capabilities on the Hierarchy tab to see results here.</p>
        </EmptyState>
      );
    }

    if (emptyReason === 'scored-only-empty') {
      return (
        <EmptyState>
          <p>No scored capabilities to show.</p>
          <p>
            Turn on &ldquo;Show capabilities without scores&rdquo; to include
            unscored capabilities, or adjust the capability filter.
          </p>
        </EmptyState>
      );
    }

    return (
      <EmptyState>
        <p>No capabilities selected.</p>
        <p>Select capabilities in the filter above to show charts.</p>
      </EmptyState>
    );
  }

  return (
    <div className={styles['charts-super-grid']}>
      {elements.map((element) => (
        <React.Fragment key={element.key}>
          <h3 className={styles['section-title']}>
            <span className={`palette-chart-${element.paletteIndex ?? 2}`}>
              <Icon
                icon={faSquare}
                className={`palette-text ${styles['section-swatch']}`}
              />
            </span>
            {element.name || element.key}
          </h3>
          {capabilities.map((capability, i) => (
            <CapabilityOverviewBar
              key={`${element.key}-${capability.id}`}
              capability={capability}
              element={element}
              metrics={
                scoresByCapability?.[capability.id]?.[element.key] ?? null
              }
              capabilitySetName={capabilitySetName}
              threshold={enabledThresholdValue(thresholds?.[element.key])}
              itemIndex={i}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}
